import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { T } from "@repo/transifex";
import { cn } from "~/lib/utils";

import { type NutritionServingBasis } from "./nutrition-climate-utils";

const OPTIONS: { value: NutritionServingBasis; label: ReactNode }[] = [
  { value: "serving", label: <T _str="Per serving" /> },
  { value: "100g", label: <T _str="Per 100 g" /> }
];

type ServingBasisToggleProps = {
  value: NutritionServingBasis;
  onChange: (value: NutritionServingBasis) => void;
  className?: string;
};

/**
 * Dropdown to switch all displayed values between "per serving" and "per 100 g".
 * Self-contained: holds its own open state and closes on select / outside click
 * (same click-outside pattern as ~/components/Popover).
 */
export const ServingBasisToggle = ({
  value,
  onChange,
  className
}: ServingBasisToggleProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const active = OPTIONS.find((option) => option.value === value) ?? OPTIONS[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [open]);

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => {
          setOpen((prev) => !prev);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 text-lg font-semibold text-kiosk-text-secondary"
      >
        {active.label}
        <FontAwesomeIcon
          icon={faChevronDown}
          className={cn("text-lg transition-transform", open && "rotate-180")}
        />
      </button>

      {open ? (
        <ul
          role="listbox"
          className="absolute right-0 top-full z-10 mt-2 w-max overflow-hidden rounded-lg bg-kiosk-background-primary shadow-2xl"
        >
          {OPTIONS.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full whitespace-nowrap px-5 py-4 text-left text-lg transition-colors hover:bg-kiosk-background-secondary",
                  option.value === value
                    ? "font-semibold text-kiosk-text-primary"
                    : "text-kiosk-text-secondary"
                )}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
